import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ISystemSettings } from '../../interfaces/system-settings';
import { IRescheduleOrders } from '../../interfaces/task-info';
import { MapService } from '../../services';
import { BBWTMessageService } from '../../services';
import { OrderService } from '../../services';
import { SettingsService } from '../../services';
import { Router } from '@angular/router';
import moment from 'moment';
import {
  IRescheduleTaskContext,
  IRescheduleTaskContextVehicle,
} from '../../interfaces/screen-specific/job-screen/vme/reschedule-task-context';

export interface TimeChange {
  days: number;
  hours: number;
  minutes: number;
  hasChanged: boolean;
}

@Component({
  selector: 'reschedule-task-dialog',
  templateUrl: 'reschedule-task-dialog.component.html',
  styleUrls: ['reschedule-task-dialog.component.scss'],
})
export class RescheduleTaskDialogComponent implements OnInit {
  @Input() selectedTaskId: number;

  @Input() orderId: number;

  @Input() visible: boolean;

  @Output() visibleChange = new EventEmitter<boolean>();

  public get selectedTask(): IRescheduleTaskContext {
    return this.orderTasks.find((x) => x.id === this.selectedTaskId);
  }

  public get vehicle(): IRescheduleTaskContextVehicle {
    return this.selectedTask?.vehicle;
  }

  orderTasks: IRescheduleTaskContext[] = [];

  isSearchVehicle: boolean = false;

  currentRouteTime: any;
  currentDistanceMiles: any;
  systemSettings: ISystemSettings;
  minutesInterval = 30; // default

  dateTimesChanged: boolean = false;
  showConfirmDates: any;

  previousDropoffEarly: Date;
  previousPickupEarly: Date;
  previousPickupLate: Date;
  previousDropoffLate: Date;

  diffPED: TimeChange = { days: 0, hours: 0, minutes: 0, hasChanged: false };
  diffPLD: TimeChange = { days: 0, hours: 0, minutes: 0, hasChanged: false };
  diffDED: TimeChange = { days: 0, hours: 0, minutes: 0, hasChanged: false };
  diffDLD: TimeChange = { days: 0, hours: 0, minutes: 0, hasChanged: false };

  savingReschedule: boolean = false;

  private _pickupEarliestDateTime: Date;

  public set pickupEarliestDateTime(value: Date) {
    this.trimSeconds(value);

    this._pickupEarliestDateTime = this.roundUpTime(value);
    this.dateTimesChanged = true;

    this.calcTimeChanges(this.selectedTask.pickupEarliestDateTime, value, this.diffPED);
  }

  public get pickupEarliestDateTime(): Date {
    return this._pickupEarliestDateTime;
  }

  private _pickupLatestDateTime: Date;
  public set pickupLatestDateTime(value: Date) {
    this.trimSeconds(value);
    this._pickupLatestDateTime = value;
    this.dateTimesChanged = true;

    this.calcTimeChanges(this.selectedTask.pickupLatestDateTime, value, this.diffPLD);
  }

  public get pickupLatestDateTime(): Date {
    return this._pickupLatestDateTime;
  }

  private _dropoffEarliestDateTime: Date;
  public set dropoffEarliestDateTime(value: Date) {
    this.trimSeconds(value);

    this._dropoffEarliestDateTime = value;
    this.dateTimesChanged = true;

    this.calcTimeChanges(this.selectedTask.dropoffEarliestDateTime, value, this.diffDED);
  }

  public get dropoffEarliestDateTime(): Date {
    return this._dropoffEarliestDateTime;
  }

  private _dropoffLatestDateTime: Date;
  public set dropoffLatestDateTime(value: Date) {
    this.trimSeconds(value);

    this._dropoffLatestDateTime = value;
    this.dateTimesChanged = true;

    this.calcTimeChanges(this.selectedTask.dropoffLatestDateTime, value, this.diffDLD);
  }

  public get dropoffLatestDateTime(): Date {
    return this._dropoffLatestDateTime;
  }

  constructor(
    public messageService: BBWTMessageService,
    private router: Router,
    private orderService: OrderService,
    private mapService: MapService,
    private settingsService: SettingsService,
  ) {}

  ngOnInit() {
    this.settingsService.GetSystemSettings().then((data: ISystemSettings) => {
      this.systemSettings = data;
    });
  }

  calcTimeChanges(day1, day2, displayVar: TimeChange) {
    const original = moment(day1);
    const newMoment = moment(day2);
    const duration = moment.duration(newMoment.diff(original));

    displayVar.hours = duration.hours();
    displayVar.days = duration.days();
    displayVar.minutes = duration.minutes();
    displayVar.hasChanged = duration.asMinutes() != 0;
  }

  setModelValues() {
    const t = this.selectedTask;

    this._pickupEarliestDateTime = moment(t.pickupEarliestDateTime).toDate();
    this._pickupLatestDateTime = moment(t.pickupLatestDateTime).toDate();
    this._dropoffEarliestDateTime = moment(t.dropoffEarliestDateTime).toDate();
    this._dropoffLatestDateTime = moment(t.dropoffLatestDateTime).toDate();
  }

  setRouteTime() {
    let task = this.selectedTask;

    let pickup = task.pickupLocation;
    let dropoff = task.dropoffLocation;
    this.currentDistanceMiles = this.mapService.getMiles(this.selectedTask.distance);

    const dto = this.mapService.GetRouteDTO(pickup, dropoff, this.pickupEarliestDateTime);
    this.mapService.GetRoute(dto).then((route) => {
      if (route != null) {
        this.currentRouteTime = route.duration;
      } else {
        console.log('Route time is null for: ', task);
      }
    });
  }

  alignDateTimes() {
    // ensure following dates are aligned with this one (i.e. they can't be earlier and must include travel time)

    this.pickupLatestDateTime = this.isDateEarlier(
      this.pickupLatestDateTime,
      this.pickupEarliestDateTime,
    )
      ? moment(this.pickupEarliestDateTime).toDate()
      : moment(this.pickupLatestDateTime).toDate();

    let dropoffEarly = this.addRouteTime(this.pickupEarliestDateTime);
    this.dropoffEarliestDateTime = this.isDateEarlier(this.dropoffEarliestDateTime, dropoffEarly)
      ? moment(dropoffEarly).toDate()
      : moment(this.dropoffEarliestDateTime).toDate();

    let dropoffLate = this.addRouteTime(this.pickupLatestDateTime);
    this.dropoffLatestDateTime = this.isDateEarlier(this.dropoffLatestDateTime, dropoffLate)
      ? moment(dropoffLate).toDate()
      : moment(this.dropoffLatestDateTime).toDate();
  }

  isDateEarlier(date1, date2) {
    let isEarlier = moment(date1).isBefore(date2);
    return isEarlier;
  }

  getValidPickupEarliest() {
    let earliest = moment(this.pickupEarliestDateTime).toDate();
    let defEarliest = this.getDefaultPickupEarliestDateTime();

    if (earliest < defEarliest) {
      let newEarliest = this.roundUpTime(defEarliest);
      this.trimSeconds(newEarliest);

      return newEarliest;
    }

    this.trimSeconds(earliest);
    return earliest;
  }

  getValidPickupLatest(pickupEarliest: Date) {
    let latest = moment(this.pickupLatestDateTime).toDate();
    let defLatest = this.getDefaultPickupLatestDateTime(pickupEarliest);

    if (latest < defLatest) {
      let newLatest = this.roundUpTime(defLatest);
      this.trimSeconds(newLatest);

      return newLatest;
    }

    this.trimSeconds(latest);
    return latest;
  }

  getValidDropoffEarliest(pickupEarliest: Date) {
    let earliest = moment(this.dropoffEarliestDateTime).toDate();
    let defEarliest = this.addRouteTime(pickupEarliest);

    if (earliest < defEarliest) {
      let newEarliest = this.roundUpTime(defEarliest);
      this.trimSeconds(newEarliest);

      return newEarliest;
    }

    this.trimSeconds(earliest);
    return earliest;
  }

  getValidDropoffLatest(pickupLatest: Date) {
    let latest = moment(this.dropoffLatestDateTime).toDate();
    let defLatest = this.addRouteTime(pickupLatest);

    if (latest < defLatest) {
      let newLatest = this.roundUpTime(defLatest);
      this.trimSeconds(newLatest);

      return newLatest;
    }

    this.trimSeconds(latest);
    return latest;
  }

  addRouteTime(date: Date): Date {
    return this.roundUpTime(moment(date).add(this.currentRouteTime, 's').toDate());
  }

  getDefaultPickupEarliestDateTime(): Date {
    return new Date();
  }

  getDefaultPickupLatestDateTime(pickupEarliestDateTime: Date): Date {
    let createOrderTimeDayStart = this.systemSettings.createOrderTimeDayStart;
    let createOrderTimeDayEnd = this.systemSettings.createOrderTimeDayEnd;
    let createOrderPickUpLatestPlusDays = this.systemSettings.createOrderPickUpLatestPlusDays;
    let createOrderAfterEndPickUpLatestPlusDays =
      this.systemSettings.createOrderAfterEndPickUpLatestPlusDays;

    let pickupEarliestDateTimeMoment = moment(pickupEarliestDateTime);
    if (createOrderTimeDayStart) {
      let createOrderTimeDayStartMoment = moment(pickupEarliestDateTime)
        .startOf('day')
        .add(createOrderTimeDayStart, 'h');

      // 1st case
      if (pickupEarliestDateTimeMoment.isSameOrBefore(createOrderTimeDayStartMoment)) {
        if (createOrderPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime)
            .startOf('day')
            .add(createOrderPickUpLatestPlusDays, 'd')
            .add(createOrderTimeDayStart, 'h')
            .toDate();
        }
      }
    }

    if (createOrderTimeDayEnd) {
      let createOrderTimeDayEndMoment = moment(pickupEarliestDateTime)
        .startOf('day')
        .add(createOrderTimeDayEnd + 12, 'h');

      // 2nd case
      if (pickupEarliestDateTimeMoment.isSameOrBefore(createOrderTimeDayEndMoment)) {
        if (createOrderPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime).add(createOrderPickUpLatestPlusDays, 'd').toDate();
        }
      }
      // 3rd case
      else {
        if (createOrderAfterEndPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime)
            .add(createOrderAfterEndPickUpLatestPlusDays, 'd')
            .toDate();
        }
      }
    }

    // default
    return moment(pickupEarliestDateTime).add(12, 'h').toDate();
  }

  roundUpTime(time: Date) {
    this.trimSeconds(time);

    //console.log("Current minutes interval: ", this.minutesInterval);
    if (time.getMinutes() % this.minutesInterval == 0 || time.getMinutes() == 0) {
      return time;
    }

    var min = this.minutesInterval;
    while (min < 60) {
      if (min >= time.getMinutes()) {
        break;
      }

      min += this.minutesInterval;
    }

    if (min > 60) {
      time.setMinutes(60);
    } else {
      time.setMinutes(0);
    }

    return time;
  }

  trimSeconds(dt: Date) {
    if (dt) {
      dt.setSeconds(0);
      dt.setMilliseconds(0);
    }
  }

  dialogSaveTaskClick() {
    //if any of the task dates/times are outside a range that would be acceptable for a new order
    //then prompt the user with a warning and ask them if they want an auto-completed version with corrected min dates/times.

    if (!this.dateTimesChanged) {
      this.visibleChange.emit(false);
      return;
    }

    let pickupEarly = this.getValidPickupEarliest();
    let dropoffEarly = this.getValidDropoffEarliest(pickupEarly);

    // the late dates/times will default to valid from settings if selected but are also acceptable to be the same as early
    let pickupLate = this.getValidPickupLatest(pickupEarly);
    let dropoffLate = this.getValidDropoffLatest(pickupLate);

    if (
      this.pickupEarliestDateTime < pickupEarly ||
      this.pickupLatestDateTime < pickupLate ||
      this.dropoffEarliestDateTime < dropoffEarly ||
      this.dropoffLatestDateTime < dropoffLate
    ) {
      this.showConfirmDates = true;

      this.previousPickupEarly = new Date(this.selectedTask.pickupEarliestDateTime);
      this.previousPickupLate = new Date(this.selectedTask.pickupLatestDateTime);

      this.previousDropoffEarly = new Date(this.selectedTask.dropoffEarliestDateTime);
      this.previousDropoffLate = new Date(this.selectedTask.dropoffLatestDateTime);
    } else {
      this.saveTaskData();
    }
  }

  // checks and confirmations where required before actually saving
  changeAllTasks: any;
  otherTasks: number = 0;

  saveTaskData() {
    // SEND A REQUEST TO CLONE THE TASK WITH NEW DATES AND ADD IT TO THE ORDER

    let rescheduleOrders: IRescheduleOrders = {
      jobId: this.selectedTask.jobId,
      orders: [],
    };

    this.orderTasks.forEach((task) => {
      if (task.id == this.selectedTask.id || this.changeAllTasks) {
        let pickupEarliestDateTime = moment(task.pickupEarliestDateTime).add(this.diffPED);
        let pickupLatestDateTime = moment(task.pickupLatestDateTime).add(this.diffPLD);
        let dropoffEarliestDateTime = moment(task.dropoffEarliestDateTime).add(this.diffDED);
        let dropoffLatestDateTime = moment(task.dropoffLatestDateTime).add(this.diffDLD);

        rescheduleOrders.orders.push({
          tasks: [
            {
              taskId: task.id,
              pickupEarliestDateTime: pickupEarliestDateTime.format('YYYY-MM-DD HH:mm'),
              pickupLatestDateTime: pickupLatestDateTime.format('YYYY-MM-DD HH:mm'),
              dropoffEarliestDateTime: dropoffEarliestDateTime.format('YYYY-MM-DD HH:mm'),
              dropoffLatestDateTime: dropoffLatestDateTime.format('YYYY-MM-DD HH:mm'),

              originalPickupEarliestDateTime: task.pickupEarliestDateTime,
              originalPickupLatestDateTime: task.pickupLatestDateTime,
              originalDropoffEarliestDateTime: task.dropoffEarliestDateTime,
              originalDropoffLatestDateTime: task.dropoffLatestDateTime,
            },
          ],
        });
      }
    });

    this.reschedule(rescheduleOrders);
  }

  reschedule(rescheduleOrders: IRescheduleOrders) {
    this.savingReschedule = true;

    this.orderService
      .rescheduleOrders(rescheduleOrders)
      .then((newJobId) => {
        if (newJobId) {
          this.router.routeReuseStrategy.shouldReuseRoute = () => false;
          this.router.navigate(['app/jobscreen/' + newJobId]);
          setTimeout(() => {
            this.messageService.ShowSuccess('Save', 'Task was rescheduled');
          }, 2000);
        }
      })
      .catch(() => {
        this.messageService.ShowError('Save', 'Task saving error');
        this.savingReschedule = false;
        this.showConfirmDates = false;
      });
  }

  undoChanges() {
    this.showConfirmDates = false;
    this.pickupEarliestDateTime = this.previousPickupEarly;
    this.pickupLatestDateTime = this.previousPickupLate;
    this.dropoffEarliestDateTime = this.previousDropoffEarly;
    this.dropoffLatestDateTime = this.previousDropoffLate;
  }

  getTravelTime(): string {
    return this.mapService.getTravelTimeFormatted(this.selectedTask.estimateTime);
  }

  async onDialogShow() {
    this.orderTasks = await this.orderService.getRescheduleContext(this.orderId);
    this.setModelValues();
    this.setRouteTime();
  }
}
